/**
 * Pure helper: collapse a list of CheckoutBlocks into a sequence of
 * skeleton primitives. The shopper-side `NarratedAutoWalkProgress`
 * renders these as shimmer placeholders so the layout doesn't shift
 * on transition into the next page.
 *
 * Deliberately conservative: unknown block types contribute nothing
 * (forward-compat for new blocks that ship after this code paints).
 */
import type { CheckoutBlockDto } from './api.js';

/** One skeleton placeholder per visible block. */
export type SkeletonPrimitive =
  | { kind: 'heading' }
  | { kind: 'paragraph'; lines: number }
  | { kind: 'form-fields'; count: number }
  | { kind: 'button' }
  | { kind: 'method-tiles'; count: number }
  | { kind: 'summary' };

/**
 * Map each block to its skeleton primitive. Dividers / spacers / risk
 * badges / contextual processing bands contribute nothing.
 */
export function blocksToSkeleton(blocks: CheckoutBlockDto[]): SkeletonPrimitive[] {
  const out: SkeletonPrimitive[] = [];
  for (const b of blocks) {
    switch (b.type) {
      case 'heading':
        out.push({ kind: 'heading' });
        break;
      case 'paragraph':
      case 'markdown':
        out.push({ kind: 'paragraph', lines: 2 });
        break;
      case 'form-fields':
        out.push({
          kind: 'form-fields',
          count: (b as { fieldIds?: string[] }).fieldIds?.length ?? 1,
        });
        break;
      case 'lookup-form':
        out.push({ kind: 'form-fields', count: 1 });
        break;
      case 'pay-button':
        out.push({ kind: 'button' });
        break;
      case 'method-tiles':
        out.push({ kind: 'method-tiles', count: 4 });
        break;
      case 'summary':
        out.push({ kind: 'summary' });
        break;
      // dividers, spacers, callouts, risk-badge, progress-stepper, etc.
      // contribute nothing — they're chrome/affordance, not content.
    }
  }
  return out;
}
